<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('brand.brand') }}
                <template v-if="isLoaded && brand.name"> - {{ brand.name }} </template>

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'brands.list' }"
                        class="btn btn-sm btn-default"
                    >
                        <span class="sr-only">{{ $t('brand.brandsList') }}</span>
                        <span class="hidden-xs">{{ $t('brand.brandsList') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-list-ul"></i>
                        </span>
                    </router-link>

                    <router-link
                        v-if="isLoaded && !is404"
                        :to="{
                            name: 'brands.edit',
                            params: { id: brandId },
                        }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('edit') }}</span>
                        <span class="hidden-xs">{{ $t('edit') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-edit"></i>
                        </span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body with-border clearfix"
                        >
                            <img
                                class="table-row-preloader center-block"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else>
                            <div
                                v-if="is404 || !brand.id"
                                class="box-body with-border clearfix"
                            >
                                <div class="row">
                                    <div class="col-xs-12">
                                        <p>{{ $t('messages.brand.noBrand', { id: brandId }) }}</p>
                                    </div>
                                </div>
                            </div>

                            <template v-else>
                                <div class="box-header with-border">
                                    <h3 class="box-title">{{ $t('info') }}</h3>
                                </div>

                                <div class="box-body">
                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('name') }}:</strong>
                                            <p>{{ brand.name }}</p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('countryType') }}:</strong>
                                            <p>{{ brand.countryType.name }}</p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('createdAt') }}:</strong>
                                            <p>{{ formatDate(brand.createdAt) }}</p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('updatedAt') }}:</strong>
                                            <p>{{ formatDate(brand.updatedAt) }}</p>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('state.states') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.states"
                                                    block
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('state.statesExclude') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.statesExclude"
                                                    block
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('gender.genders') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.genders"
                                                    block
                                                />
                                            </p>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('provider.providers') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.providers"
                                                    block
                                                    href="/apis/providers/"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('vertical.verticals') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.verticals"
                                                    block
                                                    href="/apis/verticals/"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('api.apis') }}:</strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.apis"
                                                    block
                                                    href="/apis/"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('offer.offers') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.offers"
                                                    block
                                                    href="/offers/"
                                                />
                                            </p>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('manualDomains') }}: </strong>

                                            <p>
                                                <ValueYesNo :value="!!brand.domainsManual" />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('reusableDomains') }}: </strong>

                                            <p>
                                                <ValueYesNo :value="!!brand.domainsReusable" />
                                            </p>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('reusableShortDomainTags') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.shortDomainsTags"
                                                    block
                                                    href="/short-domains/tags/"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong> {{ $t('shortDomains.shortDomains') }}: </strong>

                                            <p>
                                                <ValueListEmpty
                                                    :list="brand.shortDomains"
                                                    block
                                                    href="/short-domains/"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('domainLimits') }}:</strong>

                                            <p>
                                                <ValueYesNo :value="!!brand.domainsLimits" />
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import ValueListEmpty from '@/components/subcomponents/ValueListEmpty';
import formatsMixin from '@/mixins/formatsMixin';

export default {
    components: {
        ValueYesNo,
        ValueListEmpty,
    },
    mixins: [formatsMixin],
    data() {
        return {
            is404: false,
            isLoaded: false,
            brandId: this.$route.params.id,
            brand: {},
            countryTypes: [],
        };
    },
    computed: {
        headTitle() {
            return this.brand.name && `${this.$t('brand.brand')} ${this.brand.name}`;
        },
    },
    async created() {
        await this.fetchCommon();
        await this.fetchBrand();
    },
    methods: {
        async fetchCommon() {
            try {
                const {
                    data: { countryTypes },
                } = await axios.get(`/brands/data/common`, {
                    params: {
                        countryTypes: true,
                    },
                });

                this.countryTypes = countryTypes || [];
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            }
        },
        async fetchBrand() {
            try {
                const {
                    data: { brand },
                } = await axios.get('/brands/data/brand', {
                    params: {
                        id: this.brandId,
                    },
                });

                brand.countryType = this.countryTypes.find((e) => e.id === brand.countryType);

                this.brand = brand;
            } catch (error) {
                this.is404 = error.response.status === 404;

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
            }
        },
    },
};
</script>
